import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../react-core/Html/SelectEditor";
import { isNullOrEmpty } from "../../../../../../react-core/Utils/Arrays";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PaymentMethod } from "../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../Shared/Typography/Typography";
import { PaymentMethodTypeOptions, PaymentMethodArraysMap } from "../../Common/Resource/Contents";
import {
    getPaymentTypeOptions,
    getSourceFromOptions,
    getSourceFromString,
    getString,
    IncomeArrangementType,
    IncomePaymentOneOffPaymentType,
    IncomePaymentSourceFrom,
} from "../Resource/Content";
import { CalculateEstimatedPaye } from "./Shared/CalculateEstimatedPaye";
import { OneOffIncomeTable } from "./Shared/OneOffIncomeTable";
import { UserAssetsAllocationData, UserAssetsAllocationTable } from "./Shared/UserAssetsAllocationTable";
import { DrawdownPaymentSourceAllocation } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentSourceAllocation";
import { OneOffIncomePaymentModel } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/OneOffIncomePaymentModel";
import { CappedPensionArrangementDetail } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/CappedPensionArrangementDetail";
import { FlexiPensionArrangementDetail } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/FlexiPensionArrangementDetail";
import { DrawdownSinglePaymentInstructionArrangementSelection } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownSinglePaymentInstructionArrangementSelection";
import { toDp } from "../../../../../../react-core/Utils/Math";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import BankDetails from "./Shared/BankDetails";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { DrawdownSinglePayment } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownSinglePayment";
import { DrawdownSinglePaymentInstruction } from "../../../../../../Wrap/Models/Domains/Drawdown/DrawdownSinglePaymentInstruction";
import { InvestmentAllocation } from "../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { RestrictionStore } from "../../Common/Restrictions/Variable";
import { User } from "../../../../../../Wrap/Models/Models/User/User";
import { UserStore } from "../../../../Stroes/Variable";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../Shared/Common/UserMessages";

interface IIncomePaymentOneOffProps extends IProcessComponentProps {}

interface IIncomePaymentOneOffPropsState {
    isTargetSpecificArrangements?: boolean;
    hasCappedCrystallisedArrangement?: boolean;
    hasFlexiCrystallisedArrangement?: boolean;
    IncomePaymentOneOffPaymentType: IncomePaymentOneOffPaymentType;
    incomePaymentOneOffSourceFrom: IncomePaymentSourceFrom;
    userInfo: User;
}
interface ITableColumn {
    title: string;
    key: string;
}

export class IncomePaymentOneOffDetail extends ProcessComponent<
    IIncomePaymentOneOffProps,
    IIncomePaymentOneOffPropsState
> {
    constructor(props: any) {
        super(props);
        const isCashOnly = this.isCashOnly();
        this.state = {
            userInfo: UserStore.getUserInfo(),
            hasCappedCrystallisedArrangement: this.getCappedList().length > 0,
            hasFlexiCrystallisedArrangement: this.getFlexiList().length > 0,
            isTargetSpecificArrangements: true,
            IncomePaymentOneOffPaymentType: isCashOnly
                ? IncomePaymentOneOffPaymentType.AvailableCash
                : IncomePaymentOneOffPaymentType.AssetsAndAvailableCash,
            incomePaymentOneOffSourceFrom: isCashOnly
                ? IncomePaymentSourceFrom.SpecifyAmount
                : IncomePaymentSourceFrom.SpecifyAssetorCash,
        };

        this.bindFunctions();
    }

    private bindFunctions() {
        this.getModel = this.getModel.bind(this);
        this.getPayment = this.getPayment.bind(this);
        this.onInstructionChange = this.onInstructionChange.bind(this);
        this.onArrangementSelectionChange = this.onArrangementSelectionChange.bind(this);
        this.onDrawdownPaymentDestinationAllocationChange = this.onDrawdownPaymentDestinationAllocationChange.bind(
            this
        );
        this.onPaymentChange = this.onPaymentChange.bind(this);
        this.onDrawdownSinglePaymentInstructionChange = this.onDrawdownSinglePaymentInstructionChange.bind(this);
        this.onPaymentAmountChange = this.onPaymentAmountChange.bind(this);
        this.onDisinvestmentAllocationsChange = this.onDisinvestmentAllocationsChange.bind(this);
    }

    public isValid(): boolean {
        const arrangementSelection = this.getArrangementSelection();
        const total = arrangementSelection.reduce((total, ele) => {
            return total + ele?.Amount?.Amount;
        }, 0);

        const isEqual = this.getPayment()?.Amount?.Amount == total;
        if (!isEqual) {
            this.props.showErrorMsgPopUp(
                ["The sum of all one-off income payment in arrangements should be equal to total value."],
                true
            );
        }
        return super.isValid() && isEqual && !RestrictionStore.WithdrawDisabled();
    }

    private onInstructionChange(newData: DrawdownSinglePaymentInstructionArrangementSelection): void {
        const { onChange, data } = this.props;
        this.onDataChange(onChange, newData, data, "Instruction");
    }

    private onArrangementSelectionChange(newData: DrawdownPaymentSourceAllocation[]): void {
        const instruction = this.getModel()?.Instruction;
        this.onDataChange(this.onInstructionChange, newData, instruction, "PaymentSourceAllocations");
    }

    private onDrawdownPaymentDestinationAllocationChange(newAllocations: DrawdownPaymentDestinationAllocation[]): void {
        const payment = this.getModel()?.Instruction?.DrawdownSinglePaymentInstruction?.Payment;
        if (payment == null) {
            this.onPaymentChange({ PaymentDestinationAllocations: newAllocations });
            return;
        }
        this.onDataChange(this.onPaymentChange, newAllocations, payment, "PaymentDestinationAllocations");
    }

    private onPaymentChange(newData: DrawdownSinglePayment): void {
        const instruction = this.getModel()?.Instruction?.DrawdownSinglePaymentInstruction;
        this.onDataChange(this.onDrawdownSinglePaymentInstructionChange, newData, instruction, "Payment");
    }

    private onDrawdownSinglePaymentInstructionChange(newData: DrawdownSinglePaymentInstruction): void {
        const instruction = this.getModel()?.Instruction;
        this.onDataChange(this.onInstructionChange, newData, instruction, "DrawdownSinglePaymentInstruction");
    }

    private onPaymentAmountChange(newData: number): void {
        const payment = this.getModel()?.Instruction?.DrawdownSinglePaymentInstruction?.Payment;

        if (payment == null) {
            this.onPaymentChange({ Amount: { Amount: newData, Currency: CurrencyCode.GBP } });
            return;
        }

        let newPaymentAmount: Money;

        if (payment.Amount) {
            newPaymentAmount = { Amount: newData, Currency: payment.Amount.Currency };
        } else {
            newPaymentAmount = { Amount: newData, Currency: CurrencyCode.GBP };
        }

        this.onDataChange(this.onPaymentChange, newPaymentAmount, payment, "Amount");
    }

    private onDisinvestmentAllocationsChange(newAllocation: Money, productId: number): void {
        const instruction = this.getModel()?.Instruction?.DrawdownSinglePaymentInstruction;

        const unchangedAllocation =
            instruction?.DisinvestmentAllocations?.filter((x) => x.ProductId != productId) ?? [];
        const allocation: InvestmentAllocation = { ProductId: productId, Value: newAllocation };
        unchangedAllocation.push(allocation);

        this.onDataChange(
            this.onDrawdownSinglePaymentInstructionChange,
            unchangedAllocation,
            instruction,
            "DisinvestmentAllocations"
        );
    }

    public hasInheritedDrawdowns(): boolean {
        const flexi = this.getFlexiList().some((a) => a?.PensionArrangementDetail?.IsInherited);
        const capped = this.getCappedList().some((a) => a?.PensionArrangementDetail?.IsInherited);
        return flexi || capped;
    }

    private isCashOnly() {
        const model = this.getModel();
        const disinvestmentAllocations =
            model?.Instruction?.DrawdownSinglePaymentInstruction?.DisinvestmentAllocations ?? [];

        return (
            disinvestmentAllocations.length == 0 ||
            (disinvestmentAllocations.length == 1 &&
                disinvestmentAllocations.some((x) => x.ProductId == model.CashInstrument.ProductId))
        );
    }

    private getPayment(): DrawdownSinglePayment {
        return this.getModel().Instruction?.DrawdownSinglePaymentInstruction?.Payment;
    }

    private getFlexiList(): FlexiPensionArrangementDetail[] {
        return this.getModel().FlexiPensionArrangements ?? [];
    }

    private getCappedList(): CappedPensionArrangementDetail[] {
        return this.getModel().CappedPensionArrangements ?? [];
    }

    private getArrangementSelection(): DrawdownPaymentSourceAllocation[] {
        return this.getModel().Instruction?.PaymentSourceAllocations ?? [];
    }

    private getModel(): OneOffIncomePaymentModel {
        return this.props.data;
    }

    private getDataForAssetsAllocationTable(): UserAssetsAllocationData[] {
        const model = this.getModel();
        const accountHoldings = model.Holdings ?? [];
        const allocations = accountHoldings.map((h) => {
            const availableValue = toDp((h.MarketValue.Amount / h.Quantity) * h.AvailableQuantity, 2);
            const availableMarketValue: Money = { Amount: availableValue, Currency: h.MarketValue.Currency };

            return {
                AssetId: h.ProductId,
                Investment: h.ProductCode,
                AvailableMarketValue: availableMarketValue,
                Allocation: null,
            };
        });
        const disinvestmentAllocations =
            model.Instruction?.DrawdownSinglePaymentInstruction?.DisinvestmentAllocations ?? [];

        if (isNullOrEmpty(disinvestmentAllocations)) {
            return allocations;
        }

        const disinvestmentDict = {};
        disinvestmentAllocations.forEach((x) => (disinvestmentDict[x.ProductId] = x));

        allocations.forEach((a) => {
            const disinvestment = disinvestmentDict[a.AssetId];
            if (disinvestment) {
                a.Allocation = disinvestment.Value;
            }
        });

        return allocations;
    }

    private clientDetailRender() {
        const data = this.getModel();

        const config = [
            {
                title: "Total estimated value available",
                data: MoneyDisplayHelper.getMoneyFormat(data.TotalEstimatedValueAvailable),
            },
            {
                title: "Which is made up of:",
                data: " ",
            },
            {
                title: "Available cash",
                data: MoneyDisplayHelper.getMoneyFormat(data.CashAvailable),
            },
            {
                title: "Estimated asset value available",
                data: MoneyDisplayHelper.getMoneyFormat(data.EstimatedAssetValueAvailable),
            },
        ];
        return (
            <div>
                <div className="d-flex flex-space">
                    <h2 className="mb-11">Income payment details</h2>
                </div>

                <Typography type="common" config={config}></Typography>
            </div>
        );
    }

    render() {
        const { onChange } = this.props;
        const data = this.getModel();

        let restrictedMessage = "An account with suspended or restricted activities cannot setup withdrawals";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        return (
            <>
                {RestrictionStore.WithdrawDisabled() && (
                    <Alert alertType={AlertType.Error} message={restrictedMessage} />
                )}
                {this.clientDetailRender()}
                <hr className="heading-underlined-text"></hr>

                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"Do you wish to target specific arrangements?"}
                    selected={true}
                    disabled={true}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => {
                        //this.onDataChange(onChange, v, data, "isTargetSpecificArrangements");
                    }}
                    required={"Please select "}
                />
                {this.hasInheritedDrawdowns() && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={
                            " SL to provide content around whether the inherited drawdowns should be targeted or excluded and they should consider the use of the standard algorithm or targeting arrangements"
                        }
                    />
                )}

                <div className="d-flex flex-space">
                    <h2 className="mb-11">Payment type</h2>
                </div>
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label=""
                    options={getPaymentTypeOptions()}
                    getText={(option) => getString(option)}
                    onChange={(v) => {
                        this.setState({ IncomePaymentOneOffPaymentType: v });
                        if (v == IncomePaymentOneOffPaymentType.AvailableCash) {
                            this.setState({ incomePaymentOneOffSourceFrom: IncomePaymentSourceFrom.SpecifyAmount });
                        }
                    }}
                    selected={this.state.IncomePaymentOneOffPaymentType}
                    required={"Please select Payment Type"}
                />

                <div className="row">
                    <div className="col-sm-4 col-12 pb-20">
                        <h3 className="mb-11">Maximum estimated income payment available from the pension</h3>
                    </div>
                    <div className="col-sm-4 col-12">
                        <span>
                            {this.state.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AvailableCash
                                ? MoneyDisplayHelper.getMoneyFormat(data.CashAvailable)
                                : MoneyDisplayHelper.getMoneyFormat(data.TotalEstimatedValueAvailable)}
                        </span>
                    </div>
                </div>
                <div className="d-flex flex-space">
                    <h2 className="mb-11">Payment amount from the pension</h2>
                </div>
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label=""
                    disabled={this.state.IncomePaymentOneOffPaymentType == IncomePaymentOneOffPaymentType.AvailableCash}
                    options={getSourceFromOptions()}
                    getText={(option) => getSourceFromString(option)}
                    onChange={(v) => {
                        this.setState({ incomePaymentOneOffSourceFrom: v });
                    }}
                    alwaysValidateOnUpdate={false}
                    selected={this.state.incomePaymentOneOffSourceFrom}
                    required={"Please select Payment Source"}
                />

                <div className="col-md-4 col-12">
                    <MoneyTextBoxEditor
                        required={"Please enter payment amount"}
                        value={this.getPayment()?.Amount?.Amount ?? 0}
                        currency={this.getPayment()?.Amount?.Currency ?? CurrencyCode.GBP}
                        label={""}
                        alwaysValidateOnUpdate={false}
                        onChange={(v) => {
                            this.onPaymentAmountChange(v);
                        }}
                        validationRules={[
                            {
                                errorMessage: "value must be > 0",
                                validator: (v) => v > 0,
                            },
                            {
                                errorMessage:
                                    "value can't be greater than maximum estimated income payment available from the pension",
                                validator: (v) => {
                                    if (
                                        this.state.IncomePaymentOneOffPaymentType ==
                                        IncomePaymentOneOffPaymentType.AvailableCash
                                    ) {
                                        return v <= data.CashAvailable.Amount;
                                    } else {
                                        return v <= data.TotalEstimatedValueAvailable.Amount;
                                    }
                                },
                            },
                        ]}
                    />
                </div>
                {this.state.incomePaymentOneOffSourceFrom == IncomePaymentSourceFrom.SpecifyAssetorCash && (
                    <UserAssetsAllocationTable
                        totalValue={this.getPayment()?.Amount}
                        data={this.getDataForAssetsAllocationTable()}
                        onChange={this.onDisinvestmentAllocationsChange}
                        onRef={(ref) => this.appendChild(ref)}
                        showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                    />
                )}

                {this.state.hasCappedCrystallisedArrangement && (
                    <OneOffIncomeTable
                        cssWrapper=""
                        tableTitle="Capped"
                        ArrangementType={IncomeArrangementType.Capped}
                        data={this.getCappedList()}
                        arrangementSelection={this.getArrangementSelection()}
                        onArrangementSelectionChange={this.onArrangementSelectionChange}
                    />
                )}
                {this.state.hasFlexiCrystallisedArrangement && (
                    <OneOffIncomeTable
                        cssWrapper=""
                        tableTitle="Flexi"
                        ArrangementType={IncomeArrangementType.Flexi}
                        data={this.getFlexiList()}
                        arrangementSelection={this.getArrangementSelection()}
                        onArrangementSelectionChange={this.onArrangementSelectionChange}
                    />
                )}

                <div className="row">
                    <div className="col-sm-4 col-12 pb-20">
                        <SelectEditor
                            label={"Payment method"}
                            options={PaymentMethodTypeOptions}
                            selected={PaymentMethod.Bacs}
                            getText={(v) => {
                                return PaymentMethodArraysMap[v];
                            }}
                            onChange={(v) => {
                                this.onDataChange(onChange, v, data, "PaymentMethod");
                            }}
                        />
                    </div>
                </div>

                <CalculateEstimatedPaye />
                <hr className="heading-underlined-text"></hr>
                {
                    <BankDetails
                        ownerHierarchyId={data?.Instruction?.DrawdownSinglePaymentInstruction?.HierarchyId}
                        allBankAccounts={data?.AllBankAccounts ?? []}
                        incomePaymentAllocations={
                            data?.Instruction?.DrawdownSinglePaymentInstruction?.Payment
                                ?.PaymentDestinationAllocations ?? []
                        }
                        onChange={this.onDrawdownPaymentDestinationAllocationChange}
                        onRef={(ref) => this.appendChild(ref)}
                        data={data}
                    />
                }
            </>
        );
    }
}
